import { Button, CardinalLocation, Tooltip, TruncatedText } from "@hex-insights/core";
import {
	FormState,
	FormTooltipButton,
	FormTooltipButtonButtonProps,
	FormTooltipButtonTooltipProps,
} from "@hex-insights/forms";
import { AcademicTermSelectField, ClassYearSelectField, EmployeeSelectField, StudentSelectField } from "../../../Forms";
import * as HomeRoomSectionFilterFormValues from "../form-values";

const filterTooltipSideOrder: CardinalLocation[] = ["bottom", "top", "right", "left"];

type BaseFieldProps<K extends keyof HomeRoomSectionFilterFormValues.FilterFormValues> = {
	formState: FormState<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, K>>;
};

export function ClassYearIDInTooltipButton({ formState }: BaseFieldProps<"classYearIDIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{ClassYearIDInTooltipButton.Tooltip}
			{ClassYearIDInTooltipButton.Button}
		</FormTooltipButton>
	);
}

ClassYearIDInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "classYearIDIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<ClassYearIDIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

ClassYearIDInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "classYearIDIn">>) {
	const classYearIDIn = formState.formValues.classYearIDIn;

	return (
		<Button variant={classYearIDIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{classYearIDIn.length > 0 ? `${formState.formFormatFunctions.classYearIDIn(classYearIDIn)}` : "Class Year"}
			</TruncatedText>
		</Button>
	);
};

export function ClassYearIDIn({ formState }: BaseFieldProps<"classYearIDIn">) {
	return <ClassYearSelectField.MultiSelect formState={formState} name="classYearIDIn" label="Class Years" noOptional />;
}

export function AcademicTermIDInTooltipButton({ formState }: BaseFieldProps<"academicTermIDIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{AcademicTermIDInTooltipButton.Tooltip}
			{AcademicTermIDInTooltipButton.Button}
		</FormTooltipButton>
	);
}

AcademicTermIDInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "academicTermIDIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<AcademicTermIDIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

AcademicTermIDInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "academicTermIDIn">>) {
	const academicTermIDIn = formState.formValues.academicTermIDIn;

	return (
		<Button variant={academicTermIDIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{academicTermIDIn.length > 0
					? `${formState.formFormatFunctions.academicTermIDIn(academicTermIDIn)}`
					: "Academic Term"}
			</TruncatedText>
		</Button>
	);
};

export function AcademicTermIDIn({ formState }: BaseFieldProps<"academicTermIDIn">) {
	return (
		<AcademicTermSelectField.MultiSelect
			formState={formState}
			name="academicTermIDIn"
			label="Academic Terms"
			noOptional
		/>
	);
}

export function TeacherIDsInTooltipButton({ formState }: BaseFieldProps<"teacherIDsIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{TeacherIDsInTooltipButton.Tooltip}
			{TeacherIDsInTooltipButton.Button}
		</FormTooltipButton>
	);
}

TeacherIDsInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "teacherIDsIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<TeacherIDsIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

TeacherIDsInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "teacherIDsIn">>) {
	const teacherIDsIn = formState.formValues.teacherIDsIn;

	return (
		<Button variant={teacherIDsIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{teacherIDsIn.length > 0 ? `${formState.formFormatFunctions.teacherIDsIn(teacherIDsIn)}` : "Teacher"}
			</TruncatedText>
		</Button>
	);
};

export function TeacherIDsIn({ formState }: BaseFieldProps<"teacherIDsIn">) {
	return <EmployeeSelectField.MultiSelect formState={formState} name="teacherIDsIn" label="Teachers" noOptional />;
}

export function StudentIDsInTooltipButton({ formState }: BaseFieldProps<"studentIDsIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{StudentIDsInTooltipButton.Tooltip}
			{StudentIDsInTooltipButton.Button}
		</FormTooltipButton>
	);
}

StudentIDsInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "studentIDsIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<StudentIDsIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

StudentIDsInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<HomeRoomSectionFilterFormValues.FilterFormValues, "studentIDsIn">>) {
	const studentIDsIn = formState.formValues.studentIDsIn;

	return (
		<Button variant={studentIDsIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{studentIDsIn.length > 0 ? `${formState.formFormatFunctions.studentIDsIn(studentIDsIn)}` : "Student"}
			</TruncatedText>
		</Button>
	);
};

export function StudentIDsIn({ formState }: BaseFieldProps<"studentIDsIn">) {
	return <StudentSelectField.MultiSelect formState={formState} name="studentIDsIn" label="Students" noOptional />;
}
